import { print } from 'graphql';
import { gql } from 'graphql-tag';
import type { MigrationStrategies, RxCollection, RxDocument, RxJsonSchema } from 'rxdb';

import { getDateModified } from '../../utilities/utilityFunctions';
import { defaultDoc, getValueFromLocalStorage } from '../share/defaultDoc';

export interface AssetSectionMetalicPropertyValue {
  readonly dOd: number;
  readonly sAllowableUnitStress: number;
  readonly pkAssetSectionMetalicPropertyValue: number;
  readonly designCorrosionAllowance: number;
  readonly eLongitudeQualityFactor: number;
  readonly pInternalDesignGause: number;
}

export interface AssetSectionPropertyValue {
  readonly pkAssetSectionPropertyValue: number;
  readonly value: string;
  readonly fkAssetSection: number;
  readonly fkSectionPropertyDefinition: number;
}

export interface SectionCmlattributeValue {
  readonly at3: number;
  readonly at6: number;
  readonly at9: number;
  readonly at12: number;
  readonly fkSectionCmlattribute: number;
  readonly utMeasurement: number;
  readonly id: number;
}

export interface SectionCmlattribute {
  readonly cmlcode: string;
  readonly surface: string;
  readonly access: string;
  readonly active: boolean;
  readonly alarm: number;
  readonly fitting: string;
  readonly id: number;
  readonly readingUser: string;
  readonly refMeasurment: string;
  readonly cubeId: number;
  readonly dateReading: string;
  readonly sectionCmlattributeValue: [SectionCmlattributeValue];
}

export interface AssetSectionArea {
  readonly fkArea: number;
  readonly fkAssetSection: number;
  readonly pkAssetSectionArea: number;
}

export interface AssetArea {
  readonly assetId: number;
  readonly areaId: number;
  readonly pcsId: string;
  readonly assetName: string;
  readonly assetCategory: number;
  readonly site: number;
  readonly discipline: number;
  readonly layer: number;
  readonly parentAreaId: number;
  readonly parentAreaPcsId: string;
  readonly pkAssetArea: number;
  readonly areaName: string;
  readonly namePcsid: string;
  readonly fkAreaType: number;
}

export interface AssetSection {
  readonly fromMajorAsset: string;
  readonly toMajorAsset: string;
  readonly temperature: number;
  readonly ksi: number;
  readonly name: string;
  readonly description: string;
  readonly fkSectionDefinition: number;
  readonly assetSectionArea: [AssetSectionArea];
  readonly assetSectionMetalicPropertyValue: [AssetSectionMetalicPropertyValue];
  readonly assetSectionPropertyValue: [AssetSectionPropertyValue];
  readonly sectionCmlattribute: [SectionCmlattribute];
}

export interface LineandSectionPropertiesList {
  readonly assetId: string;
  readonly dateCreated: string;
  readonly dateModified: string;
  readonly siteId: number;
  readonly disciplineId: number;
  readonly assetSection: [AssetSection];
  readonly assetArea: [AssetArea];
}

export const lineandSectionPropertiesListSchema: RxJsonSchema<LineandSectionPropertiesList> = {
  type: 'object',
  version: 2,
  description: 'describes line and section list',
  primaryKey: 'assetId',
  properties: {
    assetId: { type: 'string', maxLength: 36 },
    dateCreated: { type: ['string', 'null'] },
    dateModified: { type: ['string', 'null'] },
    siteId: { type: ['number', 'null'] },
    disciplineId: { type: ['number', 'null'] },
    assetSection: {
      type: 'array',
      items: {
        type: ['object', 'null'],
        properties: {
          fromMajorAsset: { type: ['string', 'null'] },
          toMajorAsset: { type: ['string', 'null'] },
          temperature: { type: ['number', 'null'] },
          ksi: { type: ['number', 'null'] },
          name: { type: ['string', 'null'] },
          description: { type: ['string', 'null'] },
          fkSectionDefinition: { type: ['number', 'null'] },
          assetSectionArea: {
            type: 'array',
            items: {
              type: ['object', 'null'],
              properties: {
                fkArea: { type: ['number', 'null'] },
                fkAssetSection: { type: ['number', 'null'] },
                pkAssetSectionArea: { type: ['number', 'null'] },
              },
            },
          },
          assetSectionMetalicPropertyValue: {
            type: 'array',
            items: {
              type: ['object', 'null'],
              properties: {
                dOd: { type: ['number', 'null'] },
                sAllowableUnitStress: { type: ['string', 'null'] },
                pkAssetSectionMetalicPropertyValue: { type: ['number', 'null'] },
                designCorrosionAllowance: { type: ['number', 'null'] },
                eLongitudeQualityFactor: { type: ['number', 'null'] },
                pInternalDesignGause: { type: ['number', 'null'] },
              },
            },
          },
          assetSectionPropertyValue: {
            type: 'array',
            items: {
              type: ['object', 'null'],
              properties: {
                pkAssetSectionPropertyValue: { type: ['number', 'null'] },
                value: { type: ['string', 'null'] },
                fkAssetSection: { type: ['number', 'null'] },
                fkSectionPropertyDefinition: { type: ['number', 'null'] },
              },
            },
          },
          sectionCmlattribute: {
            type: 'array',
            items: {
              type: ['object', 'null'],
              properties: {
                cmlcode: { type: ['string', 'null'] },
                surface: { type: ['string', 'null'] },
                access: { type: ['string', 'null'] },
                active: { type: ['boolean', 'null'] },
                alarm: { type: ['number', 'null'] },
                fitting: { type: ['string', 'null'] },
                id: { type: ['number', 'null'] },
                readingUser: { type: ['string', 'null'] },
                refMeasurment: { type: ['string', 'null'] },
                cubeId: { type: ['number', 'null'] },
                dateReading: { type: ['string', 'null'] },
                sectionCmlattributeValue: {
                  type: 'array',
                  items: {
                    type: ['object', 'null'],
                    properties: {
                      at3: { type: ['number', 'null'] },
                      at6: { type: ['number', 'null'] },
                      at9: { type: ['number', 'null'] },
                      at12: { type: ['number', 'null'] },
                      fkSectionCmlattribute: { type: ['number', 'null'] },
                      utMeasurement: { type: ['number', 'null'] },
                      id: { type: ['number', 'null'] },
                    },
                  },
                },
              },
            },
          },
        },
      },
    },
    assetArea: {
      type: 'array',
      items: {
        type: ['object', 'null'],
        properties: {
          assetId: { type: ['number', 'null'] },
          areaId: { type: ['number', 'null'] },
          pcsId: { type: ['string', 'null'] },
          assetName: { type: ['string', 'null'] },
          assetCategory: { type: ['number', 'null'] },
          site: { type: ['number', 'null'] },
          discipline: { type: ['number', 'null'] },
          layer: { type: ['number', 'null'] },
          parentAreaId: { type: ['number', 'null'] },
          parentAreaPcsId: { type: ['string', 'null'] },
          pkAssetArea: { type: ['number', 'null'] },
          areaName: { type: ['string', 'null'] },
          namePcsid: { type: ['string', 'null'] },
          fkAreaType: { type: ['number', 'null'] },
        },
      },
    },
  },
};

export type LineandSectionPropertiesListCollection =
  RxCollection<LineandSectionPropertiesList> | null;
export type LineandSectionPropertiesListDocument = RxDocument<LineandSectionPropertiesList>;

export const lineandSectionPropertiesListMigrationStrategies: MigrationStrategies = {
  1: (oldDoc: LineandSectionPropertiesListDocument) => ({
    ...oldDoc,
    siteId: JSON.parse(getValueFromLocalStorage('selectedSite'))?.selectedSiteId || 0,
    disciplineId:
      JSON.parse(getValueFromLocalStorage('selectedDiscipline'))?.selectedDisciplineId || 0,
  }),
  2: (oldDoc: LineandSectionPropertiesListDocument) => ({
    ...oldDoc,
    assetSection: oldDoc.assetSection.map(item => {
      return { ...item, ksi: item.ksi || 0 };
    }),
  }),
};

export const lineandSectionPropertiesListPullQueryBuilder = (
  document: LineandSectionPropertiesList,
) => {
  let doc = document;
  if (!doc) {
    (doc as unknown) = { ...defaultDoc };
  }
  const query = print(gql`
    query mobileAssetSectionPropertiesListFeed(
      $siteId: Int!
      $disciplineId: Int!
      $lastRequested: DateTime
    ) {
      mobileAssetSectionPropertiesListFeed(
        siteId: $siteId
        disciplineId: $disciplineId
        lastRequested: $lastRequested
      ) {
        assetId
        dateCreated
        dateModified
        siteId
        disciplineId
        assetSection {
          fromMajorAsset
          toMajorAsset
          temperature
          ksi
          name
          description
          fkSectionDefinition
          assetSectionArea {
            fkArea
            fkAssetSection
            pkAssetSectionArea
          }
          assetSectionMetalicPropertyValue {
            dOd
            sAllowableUnitStress
            pkAssetSectionMetalicPropertyValue
            designCorrosionAllowance
            eLongitudeQualityFactor
            pInternalDesignGause
          }
          assetSectionPropertyValue {
            pkAssetSectionPropertyValue
            value
            fkAssetSection
            fkSectionPropertyDefinition
          }
          sectionCmlattribute {
            cmlcode
            surface
            access
            active
            alarm
            fitting
            id
            readingUser
            refMeasurment
            cubeId
            dateReading
            sectionCmlattributeValue {
              at3
              at6
              at9
              at12
              fkSectionCmlattribute
              utMeasurement
              id
            }
          }
        }
        assetArea {
          assetId
          areaId
          pcsId
          assetName
          assetCategory
          site
          discipline
          layer
          parentAreaId
          parentAreaPcsId
          pkAssetArea
          areaName
          namePcsid
          fkAreaType
        }
      }
    }
  `);
  return {
    query,
    variables: {
      siteId: JSON.parse(getValueFromLocalStorage('selectedSite'))?.selectedSiteId,
      disciplineId: JSON.parse(getValueFromLocalStorage('selectedDiscipline'))
        ?.selectedDisciplineId,
      lastRequested: getDateModified(
        doc?.dateModified,
        doc?.dateCreated,
        doc?.siteId,
        doc?.disciplineId,
        JSON.parse(getValueFromLocalStorage('selectedSite'))?.selectedSiteId,
        JSON.parse(getValueFromLocalStorage('selectedDiscipline'))?.selectedDisciplineId,
      ),
    },
  };
};

export default {};
